import { useMemo } from 'react'

import { useAppStore } from '@/hooks'
import { StructureDtoRedux } from '@/store/modules/node'
import { NativeMap } from '@/utils/collections'

const parseFolderOdcsTenant = (data?: string): string | undefined => {
	if (!data) {
		return undefined
	}

	try {
		const parsed = JSON.parse(data) as { odcs?: { tenant?: string } }
		const tenant = parsed.odcs?.tenant?.trim()

		return tenant || undefined
	} catch {
		return undefined
	}
}

export type EffectiveOdcsTenant = {
	effective: string
	fromFolderName?: string
	inherited: boolean
}

export const getEffectiveOdcsTenant = (
	folderId: number,
	localTenant: string | undefined,
	nodes: NativeMap<StructureDtoRedux>,
	preferWorkingData: boolean,
): EffectiveOdcsTenant => {
	if (localTenant?.trim()) {
		return { effective: localTenant.trim(), inherited: false }
	}

	let current = nodes[folderId]

	while (current?.parentStructureId) {
		const parent = nodes[current.parentStructureId]

		if (!parent) {
			break
		}

		const dataStr = preferWorkingData
			? parent.workingData || parent.data
			: parent.data
		const tenant = parseFolderOdcsTenant(dataStr)

		if (tenant) {
			return {
				effective: tenant,
				inherited: true,
				fromFolderName: parent.name,
			}
		}

		current = parent
	}

	return { effective: '', inherited: false }
}

export const useEffectiveOdcsTenant = (
	folderId: number | undefined,
	localTenant: string | undefined,
	preferWorkingData: boolean,
): EffectiveOdcsTenant => {
	const nodes = useAppStore((state) => state.node.nodes)

	return useMemo(
		() =>
			folderId == null
				? { effective: '', inherited: false }
				: getEffectiveOdcsTenant(
						folderId,
						localTenant,
						nodes,
						preferWorkingData,
					),
		[folderId, localTenant, nodes, preferWorkingData],
	)
}
